"use client";
import styles from "./SearchModal.module.scss";
import { ChangeEvent, useCallback, useState } from "react";
import { SearchType } from "@/domains/search/types";
import { searchApi, searchHistory } from "@/domains/search/service";
import { useGamesFetch } from "@/domains/games/hooks/use-games-fetch";
import SearchResult from "@/domains/search/components/search-result";
import ItemsListSearch from "@/domains/search/components/items-list";
import GameCard from "@/components/GameCard/GameCard";
import HistoryListSearch from "@/domains/search/components/history-list";
import { debounce } from "@/utils/debounce";
import { useTranslations } from "next-intl";

export default function SearchModal() {
  const fetchParams = { top: true };
  const { games } = useGamesFetch(fetchParams);
  const [loading, setLoading] = useState(false);
  const [searchList, setSearchList] = useState<SearchType>({ games: [], providers: [] });
  const [inputValue, setInputValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isShowHistory, setIsShowHistory] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const t = useTranslations("SearchModal");

  const debouncedSetNewValue = useCallback(
    debounce((value: string) => {
      searchHistory.setNewValue(value);
    }, 500),
    [],
  );

  const debouncedFetchSearch = useCallback(
    debounce(async (value: string) => {
      try {
        const data = await searchApi.fetchSearch({ params: { search: value } });
        setSearchList(data.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }, 500),
    [],
  );

  function deleteValue() {
    setInputValue("");
    setIsSearching(false);
  }

  function onHistorySelect(value: string) {
    value.length > 0 && setLoading(true);
    value.length > 0 ? setIsSearching(true) : setIsSearching(false);
    setInputValue(value);
    debouncedFetchSearch(value);
    setIsShowHistory(false);
  }
  function onBlur() {
    setTimeout(() => {
      if (!isDeleting) {
        setIsShowHistory(false);
      }
    }, 100);
  }
  function showHistory() {
    setIsShowHistory(true);
  }
  function onFocus() {
    if (inputValue.length === 0) {
      showHistory();
    }
  }
  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    onHistorySelect(value);
    debouncedSetNewValue(value);
  }
  return (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>{t("title")}</h2>
      <label className={styles.search}>
        <svg
          viewBox="0 0 16 16"
          fill="currentColor"
          width="18"
          height="100%"
          className={styles.search__icon}
        >
          <use href="/images/sprite.svg#search"></use>
        </svg>
        <input
          onChange={(e) => {
            onInputChange(e);
          }}
          value={inputValue}
          className={styles.search__input}
          onFocus={onFocus}
          onBlur={onBlur}
          type="search"
          autoFocus
          placeholder={t("placeholder")}
        />
        {isShowHistory && (
          <HistoryListSearch setIsDeleting={setIsDeleting} onSelect={onHistorySelect} />
        )}
      </label>

      <div className={styles.content}>
        <div className={styles.content__inner}>
          {isSearching && !loading && (
            <SearchResult
              key={"search-result-block"}
              deleteValue={deleteValue}
              result={searchList}
            />
          )}
          {loading && <>{t("loading")}</>}
          {!isSearching && !loading && (
            <ItemsListSearch key={"game-list-block"} items={games} Cart={GameCard} />
          )}
        </div>
      </div>
    </div>
  );
}
